import React, { useMemo } from 'react';
import { AirportBreachedThresholdInfoItemDto, AirportThresholdInfoGroupModelDto } from '../../../airports/airport-dto';
import { MessageType } from '../../../services/actor-messages';
import { ThresholdInfo } from '../../../types/thresholds';
import { useAppSelector } from '../../hooks/store-hooks';
import { useFlightWorker } from '../../hooks/use-flight-worker';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import { BreachedThresholdValueCell } from './BreachedThresholdValueCell';
import { MonitorStatusBadge } from './MonitorStatusBadge';

export default function AirportThresholds(props: {
    id: string;
    onlyShowBreached: boolean;
    columnsFilter?: 'origin' | 'destination' | 'alternate';
}) {
    const [thresholds, setThresholds] = React.useState<AirportBreachedThresholdInfoItemDto[] | null>(null);
    const displayOrder = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.displayOrder);
    const definitions = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.definitions);
    const toDisplay = useMemo(() => {
        return displayOrder
            .filter((x) => !definitions[x].disabled && !definitions[x].alertsOnly)
            .map((x) => ({ type: x, info: definitions[x] }));
    }, [displayOrder, definitions]);
    const worker = useFlightWorker();
    const showOrigin = !props.columnsFilter || props.columnsFilter === 'origin';
    const showDestination = !props.columnsFilter || props.columnsFilter === 'destination';
    const showAlternate = !props.columnsFilter || props.columnsFilter === 'alternate';

    useSafeEffect(() => {
        worker.actor
            .sendAsync({
                type: MessageType.getThresholdsForAirport,
                data: props.id
            })
            .then(setThresholds);
    }, [worker, props.id]);

    const displayTypes = useMemo(
        () =>
            props.onlyShowBreached
                ? toDisplay?.filter((item) => {
                      const threshold = thresholds?.find((x) => x.type === item.type);
                      if (!threshold) {
                          return false;
                      }
                      return (
                          threshold.default?.breachedLevel ||
                          threshold.origin?.breachedLevel ||
                          threshold.destination?.breachedLevel ||
                          threshold.alternate?.breachedLevel
                      );
                  })
                : toDisplay,
        [thresholds, props.onlyShowBreached, toDisplay]
    );

    // Wait for the threshold to load before we display anything to avoid a quick flicker with empty table
    if (!thresholds) {
        return <></>;
    }

    return (
        <table className="cobalt-table cobalt-alternate p-2" style={{ fontSize: '1.0em', minWidth: 350 }}>
            <thead>
                <tr>
                    <th className="ps-2">Name</th>
                    <th>Actual</th>
                    <th>Airport</th>
                    {showOrigin && <th>Origin</th>}
                    {showDestination && <th>Dest</th>}
                    {showAlternate && <th>DestAlt</th>}
                </tr>
            </thead>
            <tbody>
                {displayTypes.map((threshold) => {
                    const item = thresholds.find((x) => x.type === threshold.type);
                    return (
                        <tr key={threshold.type}>
                            <td className="ps-2">{threshold.info.name} </td>

                            <td>{(item && item.actualValue) || '-'}</td>
                            <td>
                                <ThresholdValue info={threshold.info} item={item?.default} />
                            </td>
                            {showOrigin && (
                                <td>
                                    <ThresholdValue info={threshold.info} item={item?.origin} />
                                </td>
                            )}
                            {showDestination && (
                                <td>
                                    <ThresholdValue info={threshold.info} item={item?.destination} />
                                </td>
                            )}
                            {showAlternate && (
                                <td>
                                    <ThresholdValue info={threshold.info} item={item?.alternate} />
                                </td>
                            )}
                        </tr>
                    );
                })}
                {displayTypes.length === 0 && (
                    <tr>
                        <td colSpan={99} className="px-2">
                            No breached thresholds.
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

function ThresholdValue({ info, item }: { info: ThresholdInfo; item?: AirportThresholdInfoGroupModelDto }) {
    if (!item) {
        return <>-</>;
    }

    return (
        <MonitorStatusBadge monitorStatus={item.monitorStatus}>
            <BreachedThresholdValueCell info={info} level={item.breachedLevel} />
        </MonitorStatusBadge>
    );
}
